import type { IconName } from '../components/ui/Icon';

/**
 * The player-facing pages.
 *
 * `wheel` and `daily` are named after the two {@link WheelId}s rather than after
 * the drops they render, and stay that way on purpose: the ids are written into
 * every save file, while the names above them are the owner's to change from the
 * console. Route id `daily` is the Premium Drop; the daily *claim* has no page
 * of its own and lives on Home.
 *
 * The strings here are what a creator who has renamed nothing sees. Anything
 * that names the currency or a wheel is rebuilt from live state in `labels.ts`
 * — editing "Earn credits" below changes only the default, not what shows.
 */
export const ROUTE_IDS = [
  'home',
  'wheel',
  'daily',
  'scratch',
  'shop',
  'rewards',
  'howto',
  'profile',
] as const;

export type RouteId = (typeof ROUTE_IDS)[number];

export interface RouteDefinition {
  id: RouteId;
  /** Label shown in the navigation rail. */
  label: string;
  /** Title shown in the top bar. */
  title: string;
  subtitle: string;
  icon: IconName;
}

export const ROUTES: Record<RouteId, RouteDefinition> = {
  home: {
    id: 'home',
    label: 'Home',
    title: 'Home',
    subtitle: 'Your Spoyl hub for today',
    icon: 'home',
  },
  wheel: {
    id: 'wheel',
    label: 'Lucky Drop',
    title: 'Lucky Drop',
    subtitle: 'Spend credits, win prizes',
    icon: 'wheel',
  },
  daily: {
    id: 'daily',
    label: 'Premium Drop',
    title: 'Premium Drop',
    subtitle: 'Higher stakes, better prizes',
    icon: 'crown',
  },
  scratch: {
    id: 'scratch',
    label: 'Scratch Card',
    title: 'Scratch Card',
    subtitle: 'Scratch to reveal a prize',
    icon: 'ticket',
  },
  shop: {
    id: 'shop',
    label: 'Earn credits',
    title: 'Earn credits',
    subtitle: 'How to get more, and your coupons',
    icon: 'coins',
  },
  rewards: {
    id: 'rewards',
    label: 'My Rewards',
    title: 'My Rewards',
    subtitle: 'What you won, and where it is',
    icon: 'gift',
  },
  howto: {
    id: 'howto',
    label: 'How To Play',
    title: 'How To Play',
    subtitle: 'Four steps, start to finish',
    icon: 'bolt',
  },
  profile: {
    id: 'profile',
    label: 'Profile',
    title: 'Profile',
    subtitle: 'Your status, and the club around you',
    icon: 'user',
  },
};

/**
 * Order used by the navigation rail.
 *
 * `scratch` is absent for the same reason as `howto` — a seventh label does not fit
 * the phone bar — and it only exists for realms that have switched the card on, so
 * it is reached from Home, where it appears beside the two drops.
 *
 * `howto` is deliberately absent: below 720px the rail becomes a bottom bar
 * that splits its width evenly between entries, and a seventh one makes every
 * label unreadable. It is reached from Home instead.
 */
export const NAV_ORDER: RouteId[] = ['home', 'wheel', 'daily', 'shop', 'rewards', 'profile'];
